import * as Dialog from "@radix-ui/react-dialog";
import type { ReactNode } from "react";

interface BaseProps {
  open: boolean;
  onClose: () => void;
  /** Accessibility label for screen readers. Required if no visible title in children. */
  ariaLabel?: string;
  /** Optional longer description for screen readers. */
  ariaDescription?: string;
  children: ReactNode;
}

interface ModalProps extends BaseProps {
  /** Tailwind classes applied to the inner content panel. Override sizing/padding as needed. */
  panelClassName?: string;
  /** Inline style applied to the panel — use for arbitrary borders / shadows that
   *  don't fit cleanly into tailwind utilities (e.g. multi-stop boxShadow). */
  panelStyle?: React.CSSProperties;
}

// Visually-hidden styling — Radix requires real <Dialog.Title> / <Dialog.Description>
// nodes for screen readers, but we don't always want them visible. This mirrors
// the canonical sr-only utility class.
const SR_ONLY: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  border: 0,
};

/**
 * Centered modal overlay with backdrop. Use for dialogs that don't need a
 * mobile-style bottom sheet layout. Children render inside a card-style panel.
 */
export function Modal({
  open,
  onClose,
  ariaLabel,
  ariaDescription,
  panelClassName = "",
  panelStyle,
  children,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content
          style={panelStyle}
          className={`fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card text-foreground shadow-xl focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95 ${panelClassName}`}
        >
          <Dialog.Title style={SR_ONLY}>{ariaLabel ?? "Dialog"}</Dialog.Title>
          <Dialog.Description style={SR_ONLY}>
            {ariaDescription ?? ariaLabel ?? "Dialog content"}
          </Dialog.Description>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Bottom-sheet modal. Slides up from the bottom of the viewport. Use for
 * mobile-like pickers (bin picker, action menus) and existing lots
 * BackfillSheet pattern.
 */
export function ModalSheet({
  open,
  onClose,
  ariaLabel,
  ariaDescription,
  children,
}: BaseProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 max-h-[90vh] overflow-y-auto rounded-t-2xl border-x border-t border-border bg-card text-foreground shadow-2xl focus:outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom data-[state=open]:duration-200">
          <Dialog.Title style={SR_ONLY}>
            {ariaLabel ?? "Bottom sheet"}
          </Dialog.Title>
          <Dialog.Description style={SR_ONLY}>
            {ariaDescription ?? ariaLabel ?? "Bottom sheet content"}
          </Dialog.Description>
          <div className="mx-auto mb-3 mt-3 h-1 w-9 rounded-full bg-border" />
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Re-exports for advanced cases (custom layout, nested portals, etc.). */
export { Dialog };
